// frontend/src/pages/Quotes/QuoteDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Collapse,
} from 'reactstrap';
import { quotesAPI, ordersAPI } from '../../services/api';
import PDFViewerModal from '../../components/Modals/PDFViewerModal';
import CreateManualOrderModal from '../../components/Modals/CreateManualOrderModal';
import InvoiceScheduleModal from '../../components/Modals/InvoiceScheduleModal';
import AcceptQuoteModal from '../../components/Modals/AcceptQuoteModal';
import UploadQuoteOrderModal from '../../components/Modals/UploadQuoteOrderModal';
import { parseDate } from '../../utils/dates';



function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openVersions, setOpenVersions] = useState({});
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
  const [manualOrderModalOpen, setManualOrderModalOpen] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [uploadOrderModalOpen, setUploadOrderModalOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);



  useEffect(() => {
    loadQuoteDetail();
  }, [id]);

  const loadQuoteDetail = async () => {
    try {
      setLoading(true);
      
      const quoteResponse = await quotesAPI.getById(id);
      setQuote(quoteResponse.data);
      
      if (quoteResponse.data.proposal_number) {
        const historyResponse = await quotesAPI.getProposalHistory(quoteResponse.data.proposal_number);
        setHistory(historyResponse.data.versions || []);
        
        const currentVersion = quoteResponse.data.version;
        setOpenVersions({ [`v${currentVersion}`]: true });
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      alert('Error al cargar la propuesta');
      navigate('/admin/quotes');
    } finally {
      setLoading(false);
    }
  };

  const toggleVersion = (version) => {
    setOpenVersions(prev => ({
      ...prev,
      [`v${version}`]: !prev[`v${version}`]
    }));
  };

  const openPdfViewer = (pdfUrl) => {
    console.log('🔍 PDF URL recibida:', pdfUrl);
    console.log('🔍 Quote completo:', quote);
    console.log('🔍 quote.pdf_url:', quote.pdf_url);
    setSelectedPdfUrl(pdfUrl);
    setPdfModalOpen(true);
  };
  const handleManualOrderSuccess = (order) => {
    console.log('✅ Orden manual creada, abriendo modal de programación:', order);
    
    // En flujo manual la orden aun no existe en BD; inyectamos montos base desde la cotizacion
    const quoteSubtotal = parseFloat(quote?.subtotal || 0) || 0;
    const quoteTotal = parseFloat(quote?.total || (quoteSubtotal * 1.16)) || 0;
    setSelectedOrder({
      ...order,
      is_manual: true,
      subtotal: quoteSubtotal,
      total: quoteTotal,
      scheduled_amount_total: 0,
      remaining_to_schedule: quoteSubtotal,
    });
    
    // Abrir modal de programación automáticamente
    setScheduleModalOpen(true);
  };

  const handleUploadOrderSuccess = (order) => {
    setUploadOrderModalOpen(false);
    setSelectedOrder({
      ...order,
      subtotal: parseFloat(order.subtotal || 0),
      total: parseFloat(order.total || 0),
      scheduled_amount_total: 0,
      remaining_to_schedule: parseFloat(order.subtotal || 0),
    });
    setScheduleModalOpen(true);
    loadQuoteDetail();
  };

  const handleReactivateQuote = async () => {
    if (!window.confirm('¿Reactivar esta propuesta para continuar con su aceptación?')) return;

    try {
      setReactivating(true);
      const response = await quotesAPI.reactivateQuote(quote.id);
      setQuote(response.data.quote);
      await loadQuoteDetail();
      alert('Propuesta reactivada. Ya puedes aceptar o subir la orden de compra.');
    } catch (error) {
      const message = error.response?.data?.error || 'No se pudo reactivar la propuesta';
      alert(message);
    } finally {
      setReactivating(false);
    }
  };

  const handleScheduleConfirm = async (scheduleData) => {
    try {
      // Una orden ya guardada solo necesita su programación. La orden manual
      // todavía no tiene id y se crea junto con la programación.
      if (selectedOrder?.id) {
        await ordersAPI.scheduleInvoice(selectedOrder.id, scheduleData);
        alert(`Facturación de la orden ${selectedOrder.order_number} programada exitosamente`);
        setScheduleModalOpen(false);
        setSelectedOrder(null);
        await loadQuoteDetail();
        return;
      }

      // ✅ NUEVO: Combinar datos de orden manual + schedule
      const combinedData = {
        // Datos de la orden manual (guardados en selectedOrder)
        quote_id: quote.id,
        use_internal_number: selectedOrder.use_internal_number,
        order_number: selectedOrder.order_number || '',
        confirmation_method: selectedOrder.confirmation_method,
        confirmation_date: selectedOrder.confirmation_date,
        confirmation_reference: selectedOrder.confirmation_reference || '',
        notes: selectedOrder.notes || '',
        
        // Datos del schedule
        ...scheduleData
      };
      
      console.log('📤 Enviando orden + schedule combinados:', combinedData);
      
      // ✅ Llamar al nuevo endpoint que crea ambos
      const response = await ordersAPI.createManualWithSchedule(combinedData);
      
      alert(`Orden ${response.data.order.order_number} creada y facturación programada exitosamente`);
      
      // Cerrar modal
      setScheduleModalOpen(false);
      setSelectedOrder(null);
      
      // Recargar cotización
      loadQuoteDetail();
      
    } catch (error) {
      console.error('Error al crear orden + schedule:', error);
      throw error; // El modal manejará el error
    }
  };




  const downloadPdf = (pdfUrl, filename) => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'primary', text: 'En Proceso' },
      accepted: { color: 'primary', text: 'Aceptada' },
      rejected: { color: 'danger', text: 'Rechazada' },
      archived: { color: 'secondary', text: 'Archivada' },
    };
    return badges[status] || badges.pending;
  };

  if (loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-0">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando detalle...</p>
          </div>
        </Container>
      </>
    );
  }

  if (!quote) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <Card className="shadow border-0">
            <CardBody className="text-center py-5">
              <i className="ni ni-fat-remove text-danger" style={{ fontSize: '3rem' }}></i>
              <h4 className="mt-3">Propuesta no encontrada</h4>
              <Button 
                color="primary" 
                className="btn-icon mt-3"
                onClick={() => navigate('/admin/quotes')}
              >
                <span className="btn-inner--icon">
                  <i className="ni ni-bold-left"></i>
                </span>
                <span className="btn-inner--text">Volver a Propuestas</span>
              </Button>
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  const badge = getStatusBadge(quote.status);
  const items = quote.items || {};

  return (
    <>
      {/* ✅ Header con gradiente corporativo */}
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>

      <Container fluid className="py-4">
        {/* ✅ Header con botón volver estilizado */}
        <Row className="mb-3">
          <Col>
            <Button
              color="white"
              size="sm"
              className="btn-icon shadow-sm"
              onClick={() => navigate('/admin/quotes')}
            >
              <span className="btn-inner--icon">
                <i className="ni ni-bold-left"></i>
              </span>
              <span className="btn-inner--text ms-1">Volver a Propuestas</span>
            </Button>
          </Col>
        </Row>

        {/* ✅ Card principal con shadow y sin border */}
        <Row>
          <Col lg="8">
            <Card className="shadow border-0 mb-4">
              {/* ✅ Header con gradiente y iconos */}
              <CardHeader className="bg-gradient-primary pb-3">
                <Row className="align-items-center">
                  <Col md="8">
                    <h5 className="mb-0 text-white">
                      <i className="ni ni-single-copy-04 me-2"></i>
                      {quote.proposal_number}
                    </h5>
                    <p className="text-sm text-white opacity-8 mb-0">
                      <i className="ni ni-calendar-grid-58 me-1"></i>
                      Versión {quote.version} • Creada el {parseDate(quote.created_at).toLocaleDateString('es-MX')}
                    </p>
                  </Col>
                  <Col md="4" className="text-end">
                    <Badge className={`badge-lg bg-gradient-${badge.color} shadow-sm`}>
                      {badge.text}
                    </Badge>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="p-4">
                {/* ✅ Información del cliente con iconos */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-briefcase-24 me-2"></i>
                    Información del Cliente
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Cliente:</strong> 
                        <span className="text-muted ms-2">
                          {items.client_info?.client_name || quote.client_name || 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Razón Social:</strong> 
                        <span className="text-muted ms-2">
                          {items.client_info?.legal_name || 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Account manager :</strong>
                        {quote.seller_details ? (
                          <span className="text-primary ms-2 font-weight-bold">
                            <i className="ni ni-badge me-1"></i>
                            {quote.seller_details.full_name}
                          </span>
                        ) : (
                          <span className="text-muted ms-2">
                            <i className="ni ni-fat-remove me-1"></i>
                            Sin asignar
                          </span>
                        )}
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Contacto:</strong> 
                        <span className="text-muted ms-2">
                          {items.client_info?.contact_name || quote.contact_name || 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Email:</strong> 
                        <span className="text-muted ms-2">
                          {items.client_info?.contact_email || quote.contact_email || 'N/A'}
                        </span>
                      </p>
                    </Col>
                  </Row>
                </div>

                <hr className="horizontal dark" />

                {/* ✅ Detalles del servicio */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-settings me-2"></i>
                    Detalles del Servicio
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Categoría:</strong> 
                        <Badge color="info" className="badge-sm ms-2">
                          {items.service_details?.category || quote.category || 'N/A'}
                        </Badge>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Tipo de Servicio:</strong> 
                        <span className="text-muted ms-2">
                          {items.service_details?.service_type || quote.service_type || 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Material Cobrado:</strong> 
                        <span className="text-muted ms-2">
                              ${parseFloat(items.service_details?.material_charged || items.costs?.material_charged || 0).toLocaleString('es-MX')}
                        </span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Personas:</strong> 
                        <span className="text-muted ms-2">
                          {items.service_details?.num_people || 'N/A'}
                        </span>
                    
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Grupos:</strong> 
                        <span className="text-muted ms-2">
                          {items.service_details?.num_groups || 'N/A'}
                        </span>
                        
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Nivel:</strong> 
                        <span className="text-muted ms-2">
                          {items.service_details?.administrative_level || 'N/A'}
                        </span>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Idioma:</strong> 
                        <span className="text-muted ms-2">
                          {items.service_details?.language || 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Horas:</strong> 
                        <span className="text-muted ms-2">
                          {items.service_details?.num_hours || 'N/A'}
                        </span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Sesiones:</strong> 
                        <Badge color="primary" className="badge-sm ms-2">
                          {items.service_details?.num_sessions || 'N/A'}
                        </Badge>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Precio/Hora:</strong> 
                        <span className="text-muted ms-2">
                          ${parseFloat(items.service_details?.price_per_hour || 0).toLocaleString('es-MX')}
                        </span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Incluye:</strong> 
                        <span className="text-muted ms-2">
                          {items.service_details?.includes || 'N/A'}
                        </span>
                      </p>
                    </Col>
                  </Row>
                  <p className="text-sm mb-0 mt-2">
                    <strong className="text-dark">
                      <i className="ni ni-pin-3 me-2"></i>
                      Ciudad y Estado:
                    </strong> 
                    <span className="text-muted ms-2">
                      {items.service_details?.city_state || 'N/A'}
                    </span>
                  </p>
                </div>

                <hr className="horizontal dark" />

                {/* ✅ Logística con badges */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-delivery-fast me-2"></i>
                    Logística
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Viáticos:</strong>{' '}
                        <span className="text-muted ms-2">
                          <i className={`ni ni-${items.logistics?.viaticos ? 'check-bold' : 'fat-remove'} me-1`}></i>
                          {items.logistics?.viaticos ? 'Sí' : 'No'}
                        </span>
                        
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Viáticos Cobrados:</strong>{' '}
                        <span className="text-muted ms-2">
                          <i className={`ni ni-${items.logistics?.viaticos_charged ? 'check-bold' : 'fat-remove'} me-1`}></i>
                          {items.logistics?.viaticos_charged ? 'Sí' : 'No'}

                        </span>
                        
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Lugar:</strong>{' '}
                        <span className="text-muted ms-2">
                          <i className={`ni ni-${items.logistics?.lugar ? 'check-bold' : 'fat-remove'} me-1`}></i>
                          {items.logistics?.lugar ? 'Sí' : 'No'}
                        </span>
                        
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Lugar Cobrado:</strong>{' '}
                        <span className="text-muted ms-2">
                          <i className={`ni ni-${items.logistics?.lugar_charged ? 'check-bold' : 'fat-remove'} me-1`}></i>
                          {items.logistics?.lugar_charged ? 'Sí' : 'No'}
                        </span>
                        
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Costo del Lugar:</strong> 
                        <span className="text-success ms-2 font-weight-bold">
                          ${parseFloat(items.logistics?.lugar_cost || 0).toLocaleString('es-MX')}
                        </span>
                      </p>
                    </Col>
                  </Row>
                </div>

                <hr className="horizontal dark" />

                {/* ✅ Costos */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-money-coins me-2"></i>
                    Costos 
                  </h6>
                  <Row>
                    <Col md="6">
                      
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Costo de Material:</strong> 
                        <span className="text-muted ms-2">
                          ${parseFloat(items.costs?.material_cost || 0).toLocaleString('es-MX')}
                        </span>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Otros Cobrado:</strong> 
                        <span className="text-success ms-2 font-weight-bold">
                          ${parseFloat(items.costs?.other_charged || 0).toLocaleString('es-MX')}
                        </span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Otros Costos:</strong> 
                        <span className="text-muted ms-2">
                          ${parseFloat(items.costs?.other_costs || 0).toLocaleString('es-MX')}
                        </span>
                      </p>
                    </Col>
                  </Row>
                </div>

                <hr className="horizontal dark" />

                {/* ✅ Pago con cards destacados */}
                <div>
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-credit-card me-2"></i>
                    Información de Pago
                  </h6>
                  <p className="text-sm mb-3">
                    <strong className="text-dark">Términos de Pago:</strong> 
                    <Badge color="info" className="ms-2">
                      {items.payment?.payment_terms || 'N/A'}
                    </Badge>
                  </p>
                  <Row className="mt-3">
                    <Col md="4">
                      <div className="text-center p-3 bg-light border-radius-lg shadow-sm">
                        <p className="text-xs text-uppercase mb-1 text-muted">Subtotal</p>
                        <h6 className="mb-0 text-dark">
                          ${parseFloat(quote.subtotal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </h6>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className="text-center p-3 bg-light border-radius-lg shadow-sm">
                        <p className="text-xs text-uppercase mb-1 text-muted">IVA (16%)</p>
                        <h6 className="mb-0 text-dark">
                          ${parseFloat(quote.tax || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </h6>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className="text-center p-3 bg-gradient-primary border-radius-lg shadow">
                        <p className="text-xs text-uppercase mb-1 text-white opacity-8">Total</p>
                        <h6 className="mb-0 text-white font-weight-bold">
                          ${parseFloat(quote.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </h6>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* ✅ Notas */}
                {quote.notes && (
                  <>
                    <hr className="horizontal dark" />
                    <div>
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                        <i className="ni ni-align-left-2 me-2"></i>
                        Notas
                      </h6>
                      <div className="bg-light p-3 border-radius-lg">
                        <p className="text-sm mb-0 text-dark">{quote.notes}</p>
                      </div>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* ✅ Sidebar derecho con cards estilizados */}
          <Col lg="4">
            {/* Card de archivos */}
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-gradient-primary pb-3">
                <h6 className="mb-0 text-white">
                  <i className="ni ni-folder-17 me-2"></i>
                  Archivos
                </h6>
              </CardHeader>
              <CardBody className="p-3">
                {quote.pdf_url ? (
                  <>
                    <Button
                      color="primary"
                      size="sm"
                      block
                      className="mb-2 btn-icon shadow-sm"
                      onClick={() => openPdfViewer(quote.pdf_url)}
                    >
                      <span className="btn-inner--icon">
                        <i className="ni ni-single-copy-04"></i>
                      </span>
                      <span className="btn-inner--text ms-2">Ver PDF</span>
                    </Button>
                    <Button
                      color="info"
                      size="sm"
                      block
                      className="mb-2 btn-icon shadow-sm"
                      onClick={() => downloadPdf(quote.pdf_url, `${quote.proposal_number}_v${quote.version}.pdf`)}
                    >
                      <span className="btn-inner--icon">
                        <i className="ni ni-cloud-download-95"></i>
                      </span>
                      <span className="btn-inner--text ms-2">Descargar PDF</span>
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-3">
                    <i className="ni ni-folder-17 text-muted" style={{ fontSize: '2rem' }}></i>
                    <p className="text-xs text-muted mb-0 mt-2">No hay PDF disponible</p>
                  </div>
                )}

                {quote.xml_url && (
                  <Button
                    color="secondary"
                    size="sm"
                    block
                    className="btn-icon shadow-sm"
                    onClick={() => downloadPdf(quote.xml_url, `${quote.proposal_number}_v${quote.version}.xml`)}
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-archive-2"></i>
                    </span>
                    <span className="btn-inner--text ms-2">Descargar XML</span>
                  </Button>
                )}
              </CardBody>
            </Card>

            {/* Indicador: aceptada manualmente, esperando OC */}
            {quote.accepted_manually && !quote.has_order && (
              <Card className="shadow border-0 mb-4 border-start border-warning border-3">
                <CardBody className="p-3 d-flex align-items-center gap-3">
                  <i className="ni ni-time-alarm text-warning" style={{ fontSize: '1.5rem' }}></i>
                  <div>
                    <h6 className="mb-0 text-dark">Esperando Orden de Compra</h6>
                    <small className="text-muted">
                      Propuesta aceptada manualmente. Cuando llegue la OC, súbela desde el{' '}
                      <a href="/admin/inbox/orders">Inbox de Órdenes</a> y se vinculará automáticamente.
                    </small>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Card de órdenes vinculadas (soporta múltiples OCs) */}
            {quote.orders_count > 0 && (
              <Card className="shadow border-0 mb-4">
                <CardHeader className="bg-gradient-primary pb-3">
                  <h6 className="mb-0 text-white">
                    <i className="ni ni-box-2 me-2"></i>
                    Órdenes de Compra ({quote.orders_count})
                  </h6>
                </CardHeader>
                <CardBody className="p-3">
                  {/* Barra de progreso de cobertura */}
                  {(() => {
                    const covered = parseFloat(quote.covered_amount || 0);
                    const total = parseFloat(quote.subtotal || 1);
                    const pct = Math.min((covered / total) * 100, 100);
                    return (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between text-xs mb-1">
                          <span className="text-muted">
                            Cubierto: ${covered.toLocaleString('es-MX', { minimumFractionDigits: 2 })} de ${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </span>
                          <span className={`font-weight-bold text-${quote.is_fully_covered ? 'success' : 'warning'}`}>
                            {Math.round(pct)}%
                          </span>
                        </div>
                        <div className="progress" style={{ height: '8px' }}>
                          <div
                            className={`progress-bar bg-${quote.is_fully_covered ? 'success' : 'warning'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {!quote.is_fully_covered && (
                          <small className="text-muted d-block mt-1">
                            Faltante: ${parseFloat(quote.remaining_amount || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </small>
                        )}
                      </div>
                    );
                  })()}

                  {/* Lista de OCs */}
                  {(quote.orders_list || []).map(order => {
                    const orderSubtotal = parseFloat(order.subtotal || 0);
                    const scheduledTotal = parseFloat(order.scheduled_amount_total || 0);
                    const remainingToSchedule = parseFloat(order.remaining_to_schedule || 0);
                    return (
                    <div
                      key={order.id}
                      className="mb-2 p-2 rounded"
                      style={{ background: '#f8f9fa' }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                      <div style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/orders/${order.id}`)}>
                        <strong className="text-dark">{order.order_number}</strong>
                        {order.is_manual && (
                          <Badge color="warning" className="ms-1" style={{ fontSize: '0.65rem' }}>Manual</Badge>
                        )}
                        <br />
                        <small className="text-muted">
                          {order.order_date ? parseDate(order.order_date).toLocaleDateString('es-MX') : 'Sin fecha'}
                          {' · '}${orderSubtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </small>
                      </div>
                      <i className="ni ni-bold-right text-muted"></i>
                      </div>
                      {/* ✅ Estado de facturación ya solicitada para esta OC */}
                      {scheduledTotal > 0 && (
                        <div className="mt-2">
                          <Badge color={remainingToSchedule > 0 ? 'info' : 'success'} className="me-1" style={{ fontSize: '0.65rem' }}>
                            {remainingToSchedule > 0 ? 'Factura solicitada (parcial)' : 'Factura solicitada'}
                          </Badge>
                          <small className="text-muted d-block mt-1">
                            Solicitado: ${scheduledTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })} de ${orderSubtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            {remainingToSchedule > 0 && (
                              <> · Pendiente: ${remainingToSchedule.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</>
                            )}
                          </small>
                        </div>
                      )}
                      {remainingToSchedule > 0 && (
                        <Button color="success" size="sm" block className="mt-2 mb-0" onClick={() => {
                          setSelectedOrder(order);
                          setScheduleModalOpen(true);
                        }}>
                          <i className="ni ni-calendar-grid-58 me-2"></i>
                          {scheduledTotal > 0 ? 'Solicitar factura adicional' : 'Solicitar factura'}
                        </Button>
                      )}
                    </div>
                    );
                  })}
                </CardBody>
              </Card>
            )}

            {quote.status === 'rejected' && (
              <Card className="shadow border-0 mb-4">
                <CardHeader className="bg-gradient-warning">
                  <h6 className="mb-0 text-white">
                    <i className="ni ni-button-play me-2"></i>
                    Reactivar propuesta
                  </h6>
                </CardHeader>
                <CardBody className="p-3">
                  <p className="text-sm mb-3">
                    Si el cliente aceptó después del rechazo, devuelve la propuesta a En Proceso
                    para subir su orden de compra o registrar la aceptación.
                  </p>
                  <Button
                    color="warning"
                    size="sm"
                    block
                    className="btn-icon shadow-sm"
                    disabled={reactivating}
                    onClick={handleReactivateQuote}
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-curved-next"></i>
                    </span>
                    <span className="btn-inner--text ms-2">
                      {reactivating ? 'Reactivando...' : 'Reactivar para aceptar'}
                    </span>
                  </Button>
                </CardBody>
              </Card>
            )}

            {/* Card de acciones */}
            {['pending', 'accepted'].includes(quote.status) && (
              <Card className="shadow border-0 mb-4">
                <CardHeader className="bg-gradient-primary">
                  <h6 className="mb-0 text-white">
                    <i className="ni ni-settings-gear-65 me-2"></i>
                    Acciones
                  </h6>
                </CardHeader>
                <CardBody className="p-3">
                  {/* Botón Subir Orden de Compra */}
                  <Button
                    color="info"
                    size="sm"
                    block
                    disabled={quote.is_fully_covered}
                    className="btn-icon shadow-sm mb-2"
                    onClick={() => setUploadOrderModalOpen(true)}
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-cloud-upload-96"></i>
                    </span>
                    <span className="btn-inner--text ms-2">Subir Orden de Compra</span>
                  </Button>
                  <small className="text-muted d-block text-center mb-3">
                    Sube el PDF de la OC del cliente
                  </small>

                  {/* Botón Crear Orden Manual */}
                  <Button
                    color="warning"
                    size="sm"
                    block
                    disabled={quote.is_fully_covered}
                    className="btn-icon shadow-sm mb-2"
                    onClick={() => setManualOrderModalOpen(true)}
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-fat-add"></i>
                    </span>
                    <span className="btn-inner--text ms-2">Crear Orden Manual</span>
                  </Button>
                  <small className="text-muted d-block text-center mb-3">
                    Para clientes que <strong>nunca</strong> mandarán OC
                  </small>

                  {/* Botón Aceptar sin OC */}
                  {quote.status === 'pending' && (
                    <>
                  <Button
                    color="success"
                    size="sm"
                    block
                    className="btn-icon shadow-sm mb-2"
                    onClick={() => setAcceptModalOpen(true)}
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-check-bold"></i>
                    </span>
                    <span className="btn-inner--text ms-2">Aceptar sin OC</span>
                  </Button>
                  <small className="text-muted d-block text-center mb-3">
                    Cliente confirmó — la OC llegará después
                  </small>

                  <hr className="horizontal dark my-2" />

                  {/* Botón Rechazar */}
                  <Button
                    color="danger"
                    size="sm"
                    block
                    className="btn-icon shadow-sm"
                    onClick={async () => {
                      if (window.confirm('¿Estás seguro de rechazar esta propuesta?')) {
                        try {
                          await quotesAPI.rejectQuote(quote.id);
                          alert('Propuesta rechazada exitosamente');
                          navigate('/admin/quotes');
                        } catch (error) {
                          alert('Error al rechazar propuesta');
                        }
                      }
                    }}
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-fat-remove"></i>
                    </span>
                    <span className="btn-inner--text ms-2">Rechazar Propuesta</span>
                  </Button>
                    </>
                  )}
                </CardBody>
              </Card>
            )}

          </Col>
        </Row>

        {/* ✅ Historial de versiones con diseño mejorado */}
        {history.length > 1 && (
          <Row>
            <Col lg="12">
              <Card className="shadow border-0">
                <CardHeader className="bg-gradient-primary pb-3">
                  <h6 className="mb-0 text-white">
                    <i className="ni ni-archive-2 me-2"></i>
                    Historial de Versiones ({history.length})
                  </h6>
                  <p className="text-xs text-white opacity-8 mb-0">
                    Todas las versiones de esta propuesta
                  </p>
                </CardHeader>
                <CardBody className="p-3">
                  {history.map((version, index) => {
                    const isCurrentVersion = version.version === quote.version;
                    const isOpen = openVersions[`v${version.version}`];
                    const versionBadge = getStatusBadge(version.status);

                    return (
                      <div 
                        key={version.id} 
                        className={`mb-3 ${index !== history.length - 1 ? 'border-bottom pb-3' : ''}`}
                      >
                        {/* Header de versión */}
                        <div
                          className="d-flex align-items-center justify-content-between cursor-pointer hover-shadow-sm p-2 border-radius-lg"
                          onClick={() => toggleVersion(version.version)}
                          style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                        >
                          <div className="d-flex align-items-center">
                            <div className={`icon icon-shape icon-sm ${isCurrentVersion ? 'bg-gradient-primary' : 'bg-gradient-secondary'} shadow text-center border-radius-md me-3`}>
                              <span className="text-white text-sm font-weight-bold">
                                v{version.version}
                              </span>
                            </div>
                            <div>
                              <h6 className="mb-0 text-dark">
                                Versión {version.version}
                                {isCurrentVersion && (
                                  <Badge color="primary" className="badge-sm ms-2">Actual</Badge>
                                )}
                              </h6>
                              <p className="text-xs text-muted mb-0">
                                <i className="ni ni-calendar-grid-58 me-1"></i>
                                {parseDate(version.created_at).toLocaleDateString('es-MX', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-center">
                            <Badge className={`badge-sm bg-gradient-${versionBadge.color} me-2 shadow-sm`}>
                              {versionBadge.text}
                            </Badge>
                            <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-xs text-muted`}></i>
                          </div>
                        </div>

                        {/* Contenido colapsable */}
                        <Collapse isOpen={isOpen}>
                          <div className="mt-3 ps-5">
                            {/* Cambios detectados */}
                            {version.changes && version.changes.length > 0 && (
                              <div className="mb-3">
                                <p className="text-xs text-uppercase font-weight-bolder mb-2 text-primary">
                                  <i className="ni ni-check-bold me-1"></i>
                                  Cambios respecto a la versión anterior:
                                </p>
                                <ul className="list-unstyled">
                                  {version.changes.map((change, idx) => (
                                    <li key={idx} className="text-sm mb-1">
                                                                            <i className="ni ni-check-bold text-success me-2"></i>
                                      <span className="text-dark">{change}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Información resumida */}
                            <Row className="mb-3">
                              <Col md="6">
                                <p className="text-xs mb-1">
                                  <strong className="text-dark">Total:</strong> 
                                  <span className="text-success ms-2 font-weight-bold">
                                    ${parseFloat(version.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                  </span>
                                </p>
                                <p className="text-xs mb-1">
                                  <strong className="text-dark">Categoría:</strong> 
                                  <Badge color="info" className="badge-sm ms-2">
                                    {version.category || 'N/A'}
                                  </Badge>
                                </p>
                              </Col>
                              <Col md="6">
                                <p className="text-xs mb-1">
                                  <strong className="text-dark">Servicio:</strong> 
                                  <span className="text-muted ms-2">{version.service_type || 'N/A'}</span>
                                </p>
                                <p className="text-xs mb-1">
                                  <strong className="text-dark">Account Manager:</strong> 
                                  <span className="text-muted ms-2">{version.account_manager || 'N/A'}</span>
                                </p>
                              </Col>
                            </Row>

                            {/* Botones de acción */}
                            <div className="d-flex gap-2">
                              {version.pdf_url && (
                                <>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="btn-icon shadow-sm"
                                    onClick={() => openPdfViewer(version.pdf_url)}
                                  >
                                    <span className="btn-inner--icon">
                                      <i className="ni ni-single-copy-04"></i>
                                    </span>
                                    <span className="btn-inner--text ms-1">Ver PDF</span>
                                  </Button>
                                  <Button
                                    color="info"
                                    size="sm"
                                    className="btn-icon shadow-sm"
                                    onClick={() => downloadPdf(version.pdf_url, `${quote.proposal_number}_v${version.version}.pdf`)}
                                  >
                                    <span className="btn-inner--icon">
                                      <i className="ni ni-cloud-download-95"></i>
                                    </span>
                                    <span className="btn-inner--text ms-1">Descargar</span>
                                  </Button>
                                </>
                              )}
                              {version.xml_url && (
                                <Button
                                  color="secondary"
                                  size="sm"
                                  className="btn-icon shadow-sm"
                                  onClick={() => downloadPdf(version.xml_url, `${quote.proposal_number}_v${version.version}.xml`)}
                                >
                                  <span className="btn-inner--icon">
                                    <i className="ni ni-archive-2"></i>
                                  </span>
                                  <span className="btn-inner--text ms-1">XML</span>
                                </Button>
                              )}
                            </div>
                          </div>
                        </Collapse>
                      </div>
                    );
                  })}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>

      {/* Modal visor de PDF */}
      <PDFViewerModal
        isOpen={pdfModalOpen}
        toggle={() => setPdfModalOpen(false)}
        pdfUrl={selectedPdfUrl}
        title={`${quote.proposal_number} v${quote.version}`}
      />

      {/* Modal crear orden manual */}
      <CreateManualOrderModal
        isOpen={manualOrderModalOpen}
        toggle={() => setManualOrderModalOpen(false)}
        quote={quote}
        onSuccess={handleManualOrderSuccess}
      />

      {/* Modal programar facturación (se abre automáticamente después de crear orden manual) */}
      {selectedOrder && (
        <InvoiceScheduleModal
          isOpen={scheduleModalOpen}
          toggle={() => {
            setScheduleModalOpen(false);
            setSelectedOrder(null);
          }}
          order={selectedOrder}
          quoteData={quote}
          onConfirm={handleScheduleConfirm}
        />
      )}

      {/* Modal subir OC desde cotización */}
      <UploadQuoteOrderModal
        isOpen={uploadOrderModalOpen}
        toggle={() => setUploadOrderModalOpen(false)}
        quote={quote}
        onSuccess={handleUploadOrderSuccess}
      />

      {/* Modal aceptar sin OC */}
      <AcceptQuoteModal
        isOpen={acceptModalOpen}
        toggle={() => setAcceptModalOpen(false)}
        quote={quote}
        onSuccess={(updatedQuote) => {
          setAcceptModalOpen(false);
          loadQuoteDetail();
        }}
      />
    </>
  );
}


export default QuoteDetail;
